import {
  IInvoiceRecord,
  INVOICE_SECTION_VARIANT,
  TInvoiceSectionVariant
} from 'components/InvoiceSectionOfBillingTab/InvoiceSectionOfBillingTab.types';
import BordUploaderButton from 'components/NudosComponents/BordUploaderButton/BordUploaderButton';
import {
  ACCEPTED_FILE_EXTENSION,
  BORD_UPLOADER_BUTTON_TYPE
} from 'components/NudosComponents/BordUploaderButton/BordUploaderButton.types';
import { useMemo, useState } from 'react';
import { displayErrorNotification, displaySuccessNotification } from 'utils/displayNudosStandardNotifications';
import { verifyFileDocument } from 'utils/validateFiles';
import { createURLFile } from 'services/cataloge';
import ModalUpdateCredit from 'views/business/BusinessListing/components/ModalUpdateCredit/ModalUpdateCredit';
import { calculateFutureDateWithTime } from 'utils/formatDatesAndHours';
import { updateNewCreditDate as updateNewCreditDateInOrder } from 'services/orders';
import { updateNewCreditDate as updateNewCreditDateInLogisticService } from 'services/services';
import { generateCreditInvoiceUrl } from 'services/billing/storageBillingInvoices.service';
import useInvoicesState from 'state/useInvoicesState';
import useResetData from 'hooks/useResetData';
import './InvoiceUploadButton.scss';

interface IInvoiceUploadButton {
  originalInvoiceUrl: string;
  handleUpdateOrCreateInvoice: ({ invoiceUrl }: IInvoiceRecord) => Promise<void>;
  isUpdatingOrCreatingInvoice: boolean;
  disabled?: boolean;
  hasCredit?: boolean;
  variant?: TInvoiceSectionVariant;
}

const InvoiceUploadButton = ({
  originalInvoiceUrl,
  handleUpdateOrCreateInvoice,
  isUpdatingOrCreatingInvoice,
  disabled,
  variant,
  hasCredit = false
}: IInvoiceUploadButton) => {
  const {
    logisticServiceId,
    orderId,
    storageInvoiceId,
    organizationName,
    paymentAgreementDate,
    paymentAgreementDays,
    hasActiveCredit,
    isPaymentAgreement,
    invoiceType
  } = useInvoicesState();
  const { resetOrderData, resetLSData } = useResetData();

  const [invoiceUrl, setInvoiceUrl] = useState<string | null>(originalInvoiceUrl || null);
  const [showUpdateCreditModal, setShowUpdateCreditModal] = useState<boolean>(false);
  const [newCommittedPaymentDate, setNewCommittedPaymentDate] = useState<string | null>(null);
  const [isUpdatingCredit, setIsUpdatingCredit] = useState<boolean>(false);
  const [isInvoiceLoading, setIsInvoiceLoading] = useState<boolean>(false);

  const id = invoiceType === 'logistic-service' ? logisticServiceId : orderId || 0;

  const isFilled = useMemo(() => {
    return !!invoiceUrl || (hasCredit && variant === INVOICE_SECTION_VARIANT.STORAGE);
  }, [hasCredit, variant, invoiceUrl]);

  const handleShowUpdateCreditModal = () => {
    if (isPaymentAgreement && hasActiveCredit) {
      const formattedNewDate = calculateFutureDateWithTime(paymentAgreementDays || 0);
      setNewCommittedPaymentDate(formattedNewDate);
      setShowUpdateCreditModal(true);
    }
  };

  const handleUpdateCreditDate = async (sendNotificationEmail: boolean) => {
    if (!id || !newCommittedPaymentDate || !invoiceType) {
      displayErrorNotification({
        customJSXMessage: <>No se puede actualizar la fecha de acuerdo de pago</>
      });
      return;
    }

    setIsUpdatingCredit(true);
    try {
      if (invoiceType === 'logistic-service') {
        await updateNewCreditDateInLogisticService(Number(id), newCommittedPaymentDate, sendNotificationEmail);
        resetLSData();
      } else if (invoiceType === 'order') {
        await updateNewCreditDateInOrder(Number(id), newCommittedPaymentDate, sendNotificationEmail);
        resetOrderData();
      } else {
        throw new SyntaxError('Tipo de factura no válido');
      }
      displaySuccessNotification({
        customJSXMessage: <>Actualizaste el plazo de acuerdo de pago exitosamente</>
      });
      setShowUpdateCreditModal(false);
    } catch (error) {
      if (error instanceof SyntaxError) {
        displayErrorNotification({
          customJSXMessage: <>{error.message}</>
        });
      } else {
        displayErrorNotification({
          customJSXMessage: <>Error al actualizar la fecha de acuerdo de pago</>
        });
      }
    } finally {
      setIsUpdatingCredit(false);
    }
  };

  const handleOpenDocument = (url?: string) => {
    if (!url) return;
    window.open(url, '_blank');
  };

  const handleGenerateCreditInvoiceUrl = async (storageInvoiceId?: number) => {
    try {
      if (!storageInvoiceId) return;
      const response = await generateCreditInvoiceUrl(storageInvoiceId);
      if (response) {
        return response?.invoiceUrl;
      }
    } catch (error) {
      displayErrorNotification();
      return null;
    }
  };

  const openInvoice = async (invoiceUrl?: string) => {
    setIsInvoiceLoading(true);
    try {
      const url = isFilled && !invoiceUrl ? await handleGenerateCreditInvoiceUrl(storageInvoiceId) : invoiceUrl;

      if (!url) {
        displayErrorNotification({
          customJSXMessage: <>No se pudo obtener la URL de la factura</>
        });
        return;
      }
      handleOpenDocument(url);
      await handleUpdateOrCreateInvoice({ invoiceUrl: url });
      setInvoiceUrl(url);
    } catch (error) {
      displayErrorNotification();
    } finally {
      setIsInvoiceLoading(false);
    }
  };

  const uploadInvoice = async (file?: File) => {
    if (!file) return;
    const incorrectFile = verifyFileDocument(file);
    if (incorrectFile) return;
    try {
      const fileSheet = await createURLFile(file);
      await handleUpdateOrCreateInvoice({ invoiceUrl: fileSheet.url || '' });
      setInvoiceUrl(fileSheet.url || '');
      handleShowUpdateCreditModal();
    } catch (error) {
      displayErrorNotification();
    }
  };

  const handleDeleteFile = async () => {
    try {
      await handleUpdateOrCreateInvoice({ invoiceUrl: null });
      setInvoiceUrl(null);
    } catch (error) {
      displayErrorNotification();
    }
  };

  return (
    <section className="invoiceDownloadAndSave">
      {showUpdateCreditModal && (
        <ModalUpdateCredit
          {...(invoiceType === 'logistic-service'
            ? { serviceId: String(logisticServiceId || '') }
            : { orderId: String(orderId || '') })}
          closeModal={() => setShowUpdateCreditModal(false)}
          organizationName={organizationName || ''}
          committedPaymentDate={paymentAgreementDate || ''}
          newCommittedPaymentDate={newCommittedPaymentDate}
          organizationCreditDays={paymentAgreementDays || 0}
          onUpdateCreditDate={handleUpdateCreditDate}
          isUpdating={isUpdatingCredit}
        />
      )}
      <BordUploaderButton
        isFilled={isFilled}
        disabledBordUploaderButton={disabled}
        bordUploaderButtonType={BORD_UPLOADER_BUTTON_TYPE.DEFAULT}
        title={
          <span>
            <u>Busca</u> o arrastra la factura
          </span>
        }
        emptyBordUploaderButtonSubtitle="Formatos aceptados: [formato] Peso máximo: [peso]"
        defaultFile={{
          documentName: 'Factura.pdf',
          url: invoiceUrl || ''
        }}
        handleDownloadFile={() => openInvoice(invoiceUrl || '')}
        handleChangeFile={uploadInvoice}
        handleDeleteFile={handleDeleteFile}
        customClassName="invoiceDownloadAndSaveButton"
        iconsCustomWidth="14px"
        loading={isUpdatingOrCreatingInvoice || isInvoiceLoading}
        acceptFileExtension={[ACCEPTED_FILE_EXTENSION.DOC, ACCEPTED_FILE_EXTENSION.DOCX, ACCEPTED_FILE_EXTENSION.PDF]}
      />
    </section>
  );
};

export default InvoiceUploadButton;
